import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent, Stack, Chip, Button,
  TextField, MenuItem, Table, TableHead, TableRow, TableCell, TableBody,
  LinearProgress, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider
} from '@mui/material';
import {
  Flag as FlagIcon, EmojiEvents as TrophyIcon, TrendingUp as UpIcon,
  PictureAsPdf as PdfIcon, Refresh as RefreshIcon, Save as SaveIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  getBranchPerformance, saveSalesTarget, getBranches
} from '../../services/api';
import { exportBranchPerformancePdf } from '../../utils/branchPerformancePdf';
import { isFullAdmin, canAccessAllBranches } from '../../utils/auth';
import { useBranchFilter } from '../../context/BranchContext';

const money = (n) =>
  new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR', minimumFractionDigits: 2 }).format(n || 0);

const COLORS = ['#1976d2', '#2e7d32', '#f57c00', '#7b1fa2', '#00838f', '#c62828'];

const monthOptions = [
  { value: 0, label: 'Full Year' },
  ...Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString('en', { month: 'long' })
  }))
];

const BranchTargetsPanel = () => {
  const now = new Date();
  const { selectedBranch } = useBranchFilter();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    branch_id: '',
    target_amount: '',
    notes: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [perf, br] = await Promise.all([
        getBranchPerformance({ year, month }),
        getBranches().catch(() => ({ data: [] }))
      ]);
      if (perf?.success) setData(perf.data);
      else setError(perf?.message || 'Failed to load branch performance');
      setBranches(br?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year, month]);

  useEffect(() => {
    const onBranch = () => load();
    window.addEventListener('branch-filter-changed', onBranch);
    return () => window.removeEventListener('branch-filter-changed', onBranch);
  }, [year, month]);

  const chartData = useMemo(
    () =>
      (data?.branches || []).map((b) => ({
        name: b.branch_code || b.branch_name,
        fullName: b.branch_name,
        Actual: Number(b.actual_sales) || 0,
        Target: Number(b.target_amount) || 0,
        pct: Number(b.achievement_pct) || 0
      })),
    [data]
  );

  const pieData = useMemo(
    () =>
      (data?.branches || [])
        .filter((b) => b.actual_sales > 0)
        .map((b) => ({ name: b.branch_code || b.branch_name, value: b.actual_sales })),
    [data]
  );

  const handleSaveTarget = async () => {
    try {
      setError('');
      if (!form.branch_id || form.target_amount === '') {
        setError('Branch and target amount are required');
        return;
      }
      await saveSalesTarget({
        branch_id: parseInt(form.branch_id, 10),
        year,
        month,
        target_amount: parseFloat(form.target_amount),
        notes: form.notes || null
      });
      setDialogOpen(false);
      setSuccess('Sales target saved');
      setTimeout(() => setSuccess(''), 2500);
      load();
    } catch (err) {
      setError(err.message || 'Failed to save target');
    }
  };

  const handlePdf = () => {
    if (!data) return;
    exportBranchPerformancePdf(data, {
      branchName: selectedBranch?.name,
      branchCode: selectedBranch?.branch_code,
      periodLabel: data.period?.label
    });
  };

  if (loading && !data) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const company = data?.company || {};

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight={700} color="#1565c0">Branch sales targets</Typography>
            <Typography variant="body2" color="text.secondary">
              Set targets, track who achieved them, top sales & export PDF with charts
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} sx={{ minWidth: 100 }}>
              {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Month" value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} sx={{ minWidth: 140 }}>
              {monthOptions.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            {(isFullAdmin() || canAccessAllBranches()) && (
              <Button variant="outlined" startIcon={<FlagIcon />} onClick={() => {
                setForm({
                  branch_id: selectedBranch?.id || branches[0]?.id || '',
                  target_amount: '',
                  notes: ''
                });
                setDialogOpen(true);
              }}>
                Set Target
              </Button>
            )}
            <Button variant="contained" color="error" startIcon={<PdfIcon />} onClick={handlePdf} disabled={!data}>
              Export PDF
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Company target', value: money(company.target_amount), color: '#1565c0' },
          { label: 'Company actual', value: money(company.actual_sales), color: '#2e7d32' },
          { label: 'Achievement', value: `${company.achievement_pct || 0}%`, color: company.achieved ? '#2e7d32' : '#f57c00' },
          { label: 'Targets hit', value: `${(data?.achievers || []).length} / ${(data?.branches || []).length}`, color: '#7b1fa2' }
        ].map((k) => (
          <Grid item xs={12} sm={6} md={3} key={k.label}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: k.color, mt: 0.5 }}>{k.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: 2, height: 360 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Target vs Actual by branch</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
                <Bar dataKey="Actual" fill="#1976d2" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Target" fill="#f57c00" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: 360 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Sales share</Typography>
            <ResponsiveContainer width="100%" height="90%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={100} label>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TrophyIcon color="success" />
              <Typography fontWeight={700}>Achieved target</Typography>
            </Stack>
            {(data?.achievers || []).length === 0 ? (
              <Typography color="text.secondary">No branch has hit the target yet.</Typography>
            ) : (
              (data.achievers || []).map((b) => (
                <Box key={b.branch_id} sx={{ mb: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography fontWeight={600}>{b.branch_name} ({b.branch_code})</Typography>
                    <Chip size="small" color="success" label={`${b.achievement_pct}%`} />
                  </Stack>
                  <LinearProgress variant="determinate" value={Math.min(100, b.achievement_pct)} color="success" sx={{ height: 8, borderRadius: 4, mt: 0.5 }} />
                  <Typography variant="caption">{money(b.actual_sales)} / {money(b.target_amount)}</Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <UpIcon color="primary" />
              <Typography fontWeight={700}>Top sale branch</Typography>
            </Stack>
            {data?.top_branch ? (
              <Box>
                <Typography variant="h6" fontWeight={700}>{data.top_branch.branch_name}</Typography>
                <Typography color="text.secondary">{data.top_branch.branch_code}</Typography>
                <Typography sx={{ mt: 1 }} fontWeight={700} color="#1976d2">{money(data.top_branch.actual_sales)}</Typography>
                <Typography variant="body2">{data.top_branch.bill_count} bills · {data.top_branch.achievement_pct}% of target</Typography>
                {data.top_cashier && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    Top cashier: <strong>{data.top_cashier.name}</strong> — {money(data.top_cashier.total_sales)}
                  </Alert>
                )}
              </Box>
            ) : (
              <Typography color="text.secondary">No sales in this period</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography fontWeight={700} sx={{ mb: 1 }}>All branches</Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Branch</TableCell>
              <TableCell>Code</TableCell>
              <TableCell align="right">Target</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Gap</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Top cashier</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data?.branches || []).map((b) => (
              <TableRow key={b.branch_id} hover>
                <TableCell>{b.branch_name}</TableCell>
                <TableCell>{b.branch_code}</TableCell>
                <TableCell align="right">{money(b.target_amount)}</TableCell>
                <TableCell align="right">{money(b.actual_sales)}</TableCell>
                <TableCell align="right">{money(b.gap)}</TableCell>
                <TableCell align="right">{b.achievement_pct}%</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={b.target_amount <= 0 ? 'default' : b.achieved ? 'success' : 'warning'}
                    label={b.target_amount <= 0 ? 'No target' : b.achieved ? 'Achieved' : 'Below'}
                  />
                </TableCell>
                <TableCell>{b.top_cashiers?.[0]?.name || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Top cashiers</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Bills</TableCell>
                  <TableCell align="right">Sales</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.top_cashiers || []).map((c, i) => (
                  <TableRow key={`${c.name}-${i}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell align="right">{c.bill_count}</TableCell>
                    <TableCell align="right">{money(c.total_sales)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Top products</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Sales</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.top_products || []).map((p, i) => (
                  <TableRow key={`${p.product_name}-${i}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{p.product_name}</TableCell>
                    <TableCell align="right">{p.qty}</TableCell>
                    <TableCell align="right">{money(p.total_sales)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set branch sales target</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select fullWidth label="Branch"
              value={form.branch_id}
              onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
            >
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name} ({b.branch_code})</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth type="number" label="Target amount (LKR)"
              value={form.target_amount}
              onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
            />
            <TextField
              fullWidth label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            <Divider />
            <Typography variant="caption" color="text.secondary">
              Period: {monthOptions.find((m) => m.value === month)?.label} {year}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveTarget}>Save target</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchTargetsPanel;
